import VueRouter, { NavigationGuardNext, Route } from "vue-router"

import Dashboard from "../views/Dashboard.vue"
import Login from "../views/Login.vue"
import SignUp from "../views/Signup.vue"
import Account from "../views/Account.vue"
import Search from "../views/Search.vue"
import UserProfile from "../views/UserProfile.vue"
import Organization from "../views/Organization.vue"
import { attemptRedirectToNewInterface, attemptRedirectToOldInterface, hasPermission, setApiCacheSkip } from "./guards"
import { Breadcrumb } from "@/types/breadcrumb"
import { utils } from "@/mixins/utils"
import { Store } from "vuex"
import { BaseState } from "@/store"

const routes = [
	{
		path: "/",
		name: "home",
		meta: {
			allowAnon: true,
			icon: "fa-home"
		},
		redirect: "/login"
	},
	{
		path: "/login",
		name: "login",
		component: Login,
		meta: {
			allowAnon: true,
			forceTheme: true,
			v1Only: true
		}
	},
	{
		path: "/signup",
		name: "signup",
		component: SignUp,
		meta: {
			allowAnon: true,
			icon: "fas fa-user-plus",
			breadcrumb: "Signup"
		}
	},
	{
		path: "/dashboard",
		name: "dashboard",
		component: Dashboard,
		meta: {
			icon: "fas fa-tachometer-alt",
			breadcrumb: "Dashboard",
			v1Only: false,
			allowApiCache: true
		}
	},
	{
		path: "/cases",
		name: "overview",
		component: () => import("@/views/CasesOverview.vue"),
		meta: {
			icon: "fa-newspaper",
			breadcrumb: "Overview",
			allowApiCache: true,
			v2PathName: "v2-cases"
		}
	},
	{
		path: "/cases/:caseNumber",
		name: "case",
		component: () => import("@/views/CaseDetails.vue"),
		meta: {
			breadcrumb: "Case",
			v2PathName: "v2-case-with-number",
			allowApiCache: true
		}
	},
	{
		path: "/cases/restricted/:caseNumber",
		name: "restricted-cases",
		component: () => import("@/views/RestrictedCaseDetails.vue"),
		meta: {
			breadcrumb: "Restricted Cases",
			v2PathName: "v2-restricted-case",
			allowApiCache: true
		}
	},
	{
		path: "/v2/cases",
		name: "v2-cases",
		redirect: "/v2/cases/overview",
		component: () => import("@/views/new/Cases.vue"),
		meta: {
			breadcrumb: "Records & Cases",
			breadcrumbTransform: (_route: Route, breadcrumbItem: Breadcrumb, store: Store<BaseState>): Breadcrumb | Breadcrumb[] => {
				// If the name for records and cases are the same, then don't show both
				breadcrumbItem.text = store.state.recordName === store.state.caseName
					? utils.methods.pluralize(store.state?.me?.recordName ?? "Record")
					: `${utils.methods.pluralize(store.state?.me?.recordName ?? "Record")} & ${utils.methods.pluralize(store.state?.me?.caseName ?? "Case")}`
				return breadcrumbItem
			},
			allowApiCache: true
		},
		children: [
			{
				path: "overview/:viewId?",
				name: "v2-cases-overview",
				component: () => import("@/components/New/Cases/Overview.vue"),
				meta: {
					oldPathName: "overview",
					breadcrumb: "Overview",
					breadcrumbTransform: async (route: Route, breadcrumbItem: Breadcrumb, store: any): Promise<Breadcrumb | Breadcrumb[]> => {
						// Displays the breadcrumb as Records & Cases / [Folder name]? / Case View name
						if (!route.params?.viewId) {
							return breadcrumbItem
						}

						const viewId = route.params.viewId
						const viewsService = store.state.cases?.viewsService
						const currentTenantId = store.state.me?.tenantId ?? null
						const isCurrentTenantViewState = viewsService?.tenantId === currentTenantId
						const currentView = isCurrentTenantViewState ? viewsService?.activeView : null
						const summaryViews = isCurrentTenantViewState ? viewsService?.viewSummary?.summary ?? [] : []
						let view = summaryViews.find((v: any) => v.id === viewId) ??
							(currentView?.id === viewId ? currentView : undefined)

						// Deep links can resolve breadcrumbs before views are loaded.
						// Load only when needed to avoid duplicate requests during normal in-app navigation.
						if (!view && viewsService) {
							viewsService.tenantId = currentTenantId
							viewsService.activeViewId = viewId
							await viewsService.loadViews()

							const refreshedCurrentView = viewsService.activeView
							const refreshedSummaryViews = viewsService.viewSummary?.summary ?? []
							view = refreshedSummaryViews.find((v: any) => v.id === viewId) ??
								(refreshedCurrentView?.id === viewId ? refreshedCurrentView : undefined)
						}

						if (view) {
							// If we have the view, replace the breadcrumb text (the view guid) with the actual title.
							breadcrumbItem.text = view.title

							if (view.folder) {
								// If the view has a folder, create a new breadcrumb for it.
								const folderBreadcrumb: Breadcrumb = {
									text: view.folder
								}

								return [folderBreadcrumb, breadcrumbItem]
							}
						}

						return breadcrumbItem
					},
					allowApiCache: true
				}
			},
			{
				path: ":caseNumber",
				name: "v2-case-with-number",
				component: () => import("@/components/New/Cases/CaseDetails.vue"),
				meta: {
					oldPathName: "case",
					breadcrumb: "Case Details",
					breadcrumbTransform: (route: Route, breadcrumbItem: Breadcrumb, store: Store<BaseState>): Breadcrumb | Breadcrumb[] => {
						// Displays the breadcrumb as case number or New
						breadcrumbItem.text = !!route.params["caseNumber"] && route.params["caseNumber"].toLowerCase() === "new"
							? "New"
							: `${store.state.caseName} #${route.params["caseNumber"]}`

						return breadcrumbItem
					},
					allowApiCache: true
				}
			}
		]
	},
	{
		path: "/v2/cases/restricted/:caseNumber",
		name: "v2-restricted-case",
		component: () => import("@/views/new/RestrictedCaseDetails.vue"),
		meta: {
			oldPathName: "restricted-cases",
			breadcrumb: "Restricted Cases",
			allowApiCache: true
		}
	},
	{
		path: "/account",
		name: "account",
		component: Account,
		meta: {
			breadcrumb: "Account"
		}
	},
	{
		path: "/configuration",
		name: "configuration",
		component: () => import("@/views/Configuration.vue"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "Configuration"
		}
	},
	{
		path: "/users",
		name: "users",
		component: () => import("@/views/Users.vue"),
		beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => hasPermission(to, from, next, "editData"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "Users",
			allowApiCache: true
		}
	},
	{
		path: "/insights",
		name: "insights",
		component: () => import("@/views/Insights.vue"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "Insights",
			allowApiCache: true
		}
	},
	{
		path: "/integrations",
		name: "integrations",
		component: () => import("@/views/Integrations.vue"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "Integrations",
			allowApiCache: true
		}
	},
	{
		path: "/scheduling",
		name: "scheduling",
		component: () => import("@/views/Scheduling.vue"),
		beforeEnter: (to: Route, from: Route, next: NavigationGuardNext) => hasPermission(to, from, next, "scheduling"),
		meta: {
			icon: "fa-calendar",
			breadcrumb: "Scheduling",
			allowApiCache: true
		}
	},
	{
		path: "/userprofile/:userId",
		name: "userprofile",
		component: UserProfile,
		meta: {
			icon: "fa-cog",
			breadcrumb: "User Profile",
			allowApiCache: true
		}
	},
	{
		path: "/organization",
		name: "organization",
		component: Organization,
		meta: {
			icon: "fa-cog",
			breadcrumb: "Organization"
		}
	},
	{
		path: "/search",
		name: "search",
		component: Search,
		meta: {
			icon: "fa-cog",
			breadcrumb: "Search",
			allowApiCache: true
		}
	},
	{
		path: "/system",
		name: "system",
		component: () => import("@/views/System.vue"),
		meta: {
			icon: "fa-cog",
			breadcrumb: "System"
		}
	},
	{
		// handle a non-existing route - redirect to root route
		path: "*",
		redirect: "/login"
	}
]

const router = new VueRouter({
	base: process.env.BASE_URL,
	routes
})

// Register global navigation guards
router.beforeEach(attemptRedirectToNewInterface)
router.beforeEach(attemptRedirectToOldInterface)
router.beforeEach(setApiCacheSkip)

export default router
